
import type { CharacterShadowMap } from "./charactershadow";
import { FLAG_BLOCKED } from "../sim/collision";
import { DEFAULT_SLOPE_FLOOR } from "./atmosphere";
import { FOG_FRAGMENT_GLSL, FOG_UNIFORMS, FOG_VERTEX_GLSL, type Fog } from "./camera";
import { createBuffer, createVertexArray, describeAttributes } from "./gl/buffer";
import { createProgram, uniformLocations, type Uniforms } from "./gl/shader";
import { SUN_DIR_GLSL } from "./gl/sun";
import type { TextureArray } from "./gl/texture";
import type { HeightField } from "./heightfield";
import {
  INSTANCE_BYTES,
  OFFSET_BLOCKED,
  OFFSET_CELL,
  OFFSET_LAYERS,
  buildInstances,
  instanceCount,
  type AttributeSource,
  type TileSource,
} from "./instances";
import {
  EMPTY_WINDOW,
  visibleCellWindow,
  windowContains,
  type CellGrid,
  type CellWindow,
  type ViewProjection,
  type WorldBounds,
} from "./window";

const TEXTURE_REPEAT_MM = 4000;

/** Cells of slack around the viewport. The instance buffer is rebuilt only when
 *  the camera walks a cell window out of the last built one, which at walking
 *  pace is a handful of times a second rather than sixty. */
const PAD_CELLS = 4;

/** Cells never painted in the editor. A neutral earth tone rather than black:
 *  index 0 is a hole in the export, and a hole should look like unremarkable
 *  ground rather than like the void, which means something else. */
const FALLBACK_GROUND: readonly [number, number, number] = [0.204, 0.212, 0.184];

/** Cheap 2D hash, wanted by both stages. Shared as a string so the two shaders
 *  cannot drift and produce noise that disagrees across the stage boundary. */
const HASH_GLSL = `
float hash21(vec2 p) {
  p = fract(p * vec2(127.1, 311.7));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}
`;

const VERTEX_SOURCE = `#version 300 es
precision highp float;
precision highp usampler2D;

in vec2 a_corner;
in vec2 a_cell;
in vec4 a_layer;
in vec4 a_blocked;

uniform mat4 u_viewProj;
uniform vec2 u_originMm;
uniform float u_cellMm;
uniform float u_repeatMm;
uniform highp usampler2D u_height;
// The height texture's own size, and the world size it covers. Two separate
// pairs on purpose: the bake's sample density (see heightfield.ts) has no
// fixed relationship to the map's physical extent, and folding them into one
// "metres per texel" constant would stop working the day either changes.
uniform vec2 u_heightTexels;
uniform vec2 u_worldMm;
uniform float u_heightMinMm;
uniform float u_heightSpanMm;
// The light the character shadow target was last drawn from
// (MapOutdoorRender.cpp:243-259, m_matDynamicShadow).
uniform mat4 u_characterShadowViewProj;

out vec2 v_uv;
out vec2 v_blend;
out vec2 v_worldM;
out float v_macro;
flat out vec4 v_layer;
flat out vec4 v_blocked;
out vec2 v_characterShadow;

${HASH_GLSL}
${FOG_VERTEX_GLSL}

// Value noise, for the low-frequency variation that hides the tile repeat.
float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 w = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, w.x), mix(c, d, w.x), w.y);
}

// The ground's height at a world position, in millimetres.
//
// This MUST match HeightField.at's reconstruction exactly, term for term: the
// corner convention, the clamped indices, the bilinear weights and the
// quantisation. That CPU function is what places every creature on the ground,
// and the version of this bug that ships is invisible in a screenshot -- the
// terrain looks right, the character looks right, and they are a few
// centimetres apart on a slope. texelFetch against the raw integer texture
// rather than a filtered sampler, because R16UI cannot be hardware-filtered:
// WebGL2 makes LINEAR an incomplete-texture error on an integer format, and
// even where a driver allowed it, filtering the raw sample words is a different
// curve from filtering the heights they decode to.
float groundHeightMm(vec2 worldMm) {
  vec2 grid = (worldMm / u_worldMm) * (u_heightTexels - 1.0);
  float xMax = u_heightTexels.x - 1.0;
  float yMax = u_heightTexels.y - 1.0;
  float x0 = clamp(floor(grid.x), 0.0, xMax);
  float y0 = clamp(floor(grid.y), 0.0, yMax);
  float x1 = clamp(x0 + 1.0, 0.0, xMax);
  float y1 = clamp(y0 + 1.0, 0.0, yMax);
  float fx = clamp(grid.x - x0, 0.0, 1.0);
  float fy = clamp(grid.y - y0, 0.0, 1.0);

  float h00 = float(texelFetch(u_height, ivec2(x0, y0), 0).r);
  float h10 = float(texelFetch(u_height, ivec2(x1, y0), 0).r);
  float h01 = float(texelFetch(u_height, ivec2(x0, y1), 0).r);
  float h11 = float(texelFetch(u_height, ivec2(x1, y1), 0).r);

  float top = h00 + (h10 - h00) * fx;
  float bottom = h01 + (h11 - h01) * fx;
  float raw = top + (bottom - top) * fy;

  return u_heightMinMm + (raw / 65535.0) * u_heightSpanMm;
}

void main() {
  // The half-cell offset is what puts the quad's corners on cell centres.
  vec2 worldMm = u_originMm + (a_cell + vec2(0.5) + a_corner) * u_cellMm;
  float groundMm = groundHeightMm(worldMm);
  gl_Position = u_viewProj * vec4(worldMm, groundMm, 1.0);
  // m_matDynamicShadowScale's half-scale and half-offset (MapOutdoorLoad.cpp:434-436).
  v_characterShadow = (u_characterShadowViewProj * vec4(worldMm, groundMm, 1.0)).xy * 0.5 + 0.5;

  v_uv = worldMm / u_repeatMm;
  v_blend = a_corner;
  v_worldM = worldMm * 0.001;
  // Roughly sixteen-metre features, +/- 8% brightness: enough to stop the eye
  // locking onto the four-metre tile grid, not enough to read as a stain.
  // Evaluated per vertex, not per fragment -- the feature is sixteen times wider
  // than the quad it is interpolated across, so the two are indistinguishable
  // and one of them costs four hashes on every pixel of the screen.
  v_macro = 0.92 + 0.16 * noise2(worldMm * 0.000062);
  v_layer = a_layer;
  v_blocked = a_blocked;
  // From the quad corner's own place on the ground, which is what the fade is
  // measured in -- not from the height it was displaced to, and not from the
  // depth this vertex ends up at. See FOG_VERTEX_GLSL in camera.ts.
  v_fog = fogFactor(worldMm);
}
`;

const FRAGMENT_SOURCE = `#version 300 es
precision highp float;
precision highp sampler2DArray;

in vec2 v_uv;
in vec2 v_blend;
in vec2 v_worldM;
in float v_macro;
flat in vec4 v_layer;
flat in vec4 v_blocked;
in vec2 v_characterShadow;

uniform sampler2DArray u_atlas;
uniform sampler2D u_shadow;
uniform sampler2D u_normal;
uniform vec2 u_worldM;
uniform float u_timeS;
uniform vec3 u_fallback;
uniform sampler2D u_characterShadow;
uniform float u_characterShadowOn;

out vec4 fragColour;

/** What full shade multiplies the ground by. Darker in red than in blue, so
 *  shaded ground cools rather than merely dims. */
const vec3 SHADOW_TINT = vec3(0.46, 0.50, 0.62);

/** A fixed sun, from the upper left the way every top-down strategy game since
 *  the genre existed has lit its terrain -- not because that is where this
 *  map's sun is, it has none, but because a light from screen-up-and-left is
 *  the one direction a player's visual system already expects a slope to lean
 *  away from, so a ridge reads as a ridge on the first frame instead of after
 *  conscious thought. Mostly overhead: this is relief shading for a heightfield
 *  whose slopes are gentle almost everywhere (see the bake tool's own printed
 *  stats), not a raking light for a mountain flythrough.
 *
 *  The direction itself lives in gl/sun.ts, not here, because it is no
 *  longer only the ground's: every static prop is lit against this exact same
 *  vector, and a building lit from a direction the terrain under it disagrees
 *  with would be a seam with no texture seam to blame it on. */
const vec3 SUN_DIR = ${SUN_DIR_GLSL};

/* Slope lighting floor: how bright a face turned fully away from the light is,
   as a fraction of a fully lit one, per channel. The map's own -- see
   atmosphere.ts's slopeFloorFor, which derives it from the record's Material
   and its Background light. It replaced a flat 0.6 that was the same on the
   snowfield, in the desert and in the Catacomb. */
uniform vec3 u_slopeFloor;

${HASH_GLSL}
${FOG_FRAGMENT_GLSL}

// The mip level is computed once, in main, and passed in. Two reasons, and the
// second is why this is not an implicit-LOD fetch: an explicit level is well
// defined inside the branches below, where an implicit one would be sampling
// with undefined derivatives; and it measured a quarter faster than either
// implicit LOD or explicit gradients, because the sampler stops deriving per
// pixel a level the whole quad already agrees on.
vec3 ground(float index, float lod) {
  float layer = max(index - 1.0, 0.0);
  vec3 texel = textureLod(u_atlas, vec3(v_uv, layer), lod).rgb;
  return mix(u_fallback, texel, step(0.5, index));
}

void main() {
  // The mip level the sampler would have chosen: the longer of the two texel-space
  // screen derivatives, in octaves. Taken here, at the top, where control flow is
  // still uniform and the derivatives are meaningful.
  vec2 texels = vec2(textureSize(u_atlas, 0).xy);
  vec2 dx = dFdx(v_uv) * texels;
  vec2 dy = dFdy(v_uv) * texels;
  float lod = 0.5 * log2(max(dot(dx, dx), dot(dy, dy)));

  // Dither fades to nothing at the quad's edges, where the neighbouring quad has
  // to agree with this one about the blend or a seam appears along the cell line.
  float fade = 16.0 * v_blend.x * (1.0 - v_blend.x) * v_blend.y * (1.0 - v_blend.y);
  float grain = (hash21(floor(v_worldM * 96.0)) - 0.5) * 0.6 * fade;
  float bx = smoothstep(0.0, 1.0, clamp(v_blend.x + grain, 0.0, 1.0));
  float by = smoothstep(0.0, 1.0, clamp(v_blend.y - grain, 0.0, 1.0));

  vec4 weight = vec4(
    (1.0 - bx) * (1.0 - by),
    bx * (1.0 - by),
    (1.0 - bx) * by,
    bx * by
  );

  // Fold corners that share a texture into a single sample. Most quads sit
  // wholly inside one texture and even a transition is usually between two, so
  // sampling all four unconditionally would throw away two or three fetches on
  // nearly every pixel. The comparisons are flat varyings -- one decision per
  // quad -- and the level computed above keeps every fetch defined whichever way
  // the branches go.
  vec4 w = weight;
  if (v_layer.y == v_layer.x) { w.x += w.y; w.y = 0.0; }
  if (v_layer.z == v_layer.x) { w.x += w.z; w.z = 0.0; }
  else if (v_layer.z == v_layer.y) { w.y += w.z; w.z = 0.0; }
  if (v_layer.w == v_layer.x) { w.x += w.w; w.w = 0.0; }
  else if (v_layer.w == v_layer.y) { w.y += w.w; w.w = 0.0; }
  else if (v_layer.w == v_layer.z) { w.z += w.w; w.w = 0.0; }

  vec3 colour = vec3(0.0);
  if (w.x > 0.0) colour += w.x * ground(v_layer.x, lod);
  if (w.y > 0.0) colour += w.y * ground(v_layer.y, lod);
  if (w.z > 0.0) colour += w.z * ground(v_layer.z, lod);
  if (w.w > 0.0) colour += w.w * ground(v_layer.w, lod);

  colour *= v_macro;

  // Elevation shading: a normal baked from the map's own height.raw (see
  // tools/build_terrain_normals.mjs) against a fixed sun. This is the only
  // thing standing between "flat quads with dirt textures on them" and
  // "ground with a shape" -- the atlas alone repeats the same seventeen photos
  // however the land actually rises, and no amount of texture variety reads as
  // a hill the way one consistent light direction does.
  //
  // Sampled at the normal map's own coarse resolution (one texel per two
  // world metres) rather than snapped to the nearest sample: a heightfield's
  // slope is continuous by nature, and a hard-edged sample grid half the size
  // of a footpath tile would print that grid as a visible facet on every
  // gentle rise.
  vec3 normal = normalize(texture(u_normal, v_worldM / u_worldM).xyz * 2.0 - 1.0);
  vec3 slopeLit = u_slopeFloor + (1.0 - u_slopeFloor) * clamp(dot(normal, SUN_DIR), 0.0, 1.0);
  colour *= slopeLit;

  // The map's own baked shadows: one texel per world metre, covering the world
  // exactly once, so the world position is the texture coordinate.
  //
  // Shadow is tinted rather than scaled. Ground out of the sun is still lit, by
  // the sky, and sky light is blue -- multiplying towards grey is the thing that
  // makes baked shadows read as dirt rather than as shade.
  float sun = texture(u_shadow, v_worldM / u_worldM).r;
  colour *= mix(SHADOW_TINT, vec3(1.0), sun);
  // The character shadow target, modulated in on stage 1 (MapOutdoorRenderHTP.cpp:629-640).
  colour *= mix(vec3(1.0), texture(u_characterShadow, v_characterShadow).rgb, u_characterShadowOn);


  float blocked = clamp(dot(weight, v_blocked), 0.0, 1.0);
  colour *= mix(1.0, 0.52, blocked);
  // Peaks halfway through the transition, so the darkening gains a soft lip at
  // the edge of anything impassable instead of ending in a flat step.
  float rim = 4.0 * blocked * (1.0 - blocked);
  colour *= 1.0 - 0.28 * rim;

  // Last, after every light and every tint: fog is what the air between here
  // and the eye does to the finished colour, not another term to be shaded.
  fragColour = vec4(applyFog(colour), 1.0);
}
`;

const UNIFORMS = [
  "u_viewProj",
  "u_originMm",
  "u_cellMm",
  "u_repeatMm",
  "u_height",
  "u_heightTexels",
  "u_worldMm",
  "u_heightMinMm",
  "u_heightSpanMm",
  "u_atlas",
  "u_shadow",
  "u_normal",
  "u_worldM",
  "u_timeS",
  "u_fallback",
  "u_slopeFloor",
  "u_characterShadow",
  "u_characterShadowViewProj",
  "u_characterShadowOn",
  ...FOG_UNIFORMS,
] as const;

export interface TerrainSource {
  readonly grid: CellGrid;
  readonly tiles: TileSource;
  readonly attributes: AttributeSource;
}

/** The heightfield, already uploaded. Bundled with the metadata the shader's
 *  `groundHeightMm` needs to address it, the same way `TextureArray` bundles the
 *  atlas texture with its own layer count rather than making the caller thread
 *  three separate uniforms through by hand. */
export interface HeightSource {
  readonly texture: WebGLTexture;
  readonly widthTexels: number;
  readonly heightTexels: number;
  readonly minMm: number;
  readonly spanMm: number;
}

/** Build a `HeightSource` from a `HeightField`'s own upload data. A thin
 *  adapter rather than having `GlTerrain` import `HeightField` for its shape:
 *  the field's texture getter and this shader's uniform names should not have
 *  to agree on a property name neither of them chose for the other's sake. */
export function toHeightSource(
  texture: WebGLTexture,
  field: HeightField["texture"],
): HeightSource {
  return {
    texture,
    widthTexels: field.width,
    heightTexels: field.height,
    minMm: field.minMm,
    spanMm: field.spanMm,
  };
}

export class GlTerrain {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly cornerBuffer: WebGLBuffer;
  private readonly instanceBuffer: WebGLBuffer;

  private source: TerrainSource | null = null;
  private slopeFloor: readonly [number, number, number] = DEFAULT_SLOPE_FLOOR;
  private staging = new Uint8Array(0);
  private capacityQuads = 0;
  private built: CellWindow = EMPTY_WINDOW;
  private quads = 0;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly atlas: TextureArray,
    private readonly shadow: WebGLTexture,
    private readonly normal: WebGLTexture,
    private readonly height: HeightSource,
  ) {
    this.program = createProgram(gl, VERTEX_SOURCE, FRAGMENT_SOURCE, "terrain");
    this.uniforms = uniformLocations(gl, this.program, UNIFORMS);

    this.vao = createVertexArray(gl, "terrain");
    this.cornerBuffer = createBuffer(gl, "terrain corner");
    this.instanceBuffer = createBuffer(gl, "terrain instance");

    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.cornerBuffer);
    // A unit quad as a triangle strip: four vertices, no index buffer, and the
    // corner doubles as the blend coordinate the fragment shader interpolates.
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    describeAttributes(gl, this.program, 8, [
      { name: "a_corner", components: 2, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
    ]);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    describeAttributes(gl, this.program, INSTANCE_BYTES, [
      {
        name: "a_cell",
        components: 2,
        type: gl.UNSIGNED_SHORT,
        normalized: false,
        offset: OFFSET_CELL,
        divisor: 1,
      },
      {
        name: "a_layer",
        components: 4,
        type: gl.UNSIGNED_BYTE,
        normalized: false,
        offset: OFFSET_LAYERS,
        divisor: 1,
      },
      // Normalized: the builder writes 0 or 255 and the shader wants 0 or 1 so it
      // can put the value straight through the same bilinear weights as the
      // textures, which is what makes a shoreline soft.
      {
        name: "a_blocked",
        components: 4,
        type: gl.UNSIGNED_BYTE,
        normalized: true,
        offset: OFFSET_BLOCKED,
        divisor: 1,
      },
    ]);
    gl.bindVertexArray(null);
  }

  /** Point the terrain at a map. Discards any built window: the cell indices in
   *  it belong to the previous map. */
  /** The record's own floor, from `atmosphere.ts`'s `slopeFloorFor`. Set when
   *  the environment changes, not per frame: it is a function of the record. */
  setSlopeFloor(floor: readonly [number, number, number]): void {
    this.slopeFloor = floor;
  }

  setSource(source: TerrainSource): void {
    this.source = source;
    this.built = EMPTY_WINDOW;
    this.quads = 0;
  }

  draw(
    view: ViewProjection,
    bounds: WorldBounds,
    timeSeconds: number,
    fog: Fog,
    characterShadow: CharacterShadowMap | null = null,
  ): void {
    const source = this.source;
    if (source === null) return;

    const needed = visibleCellWindow(source.grid, bounds, 1);
    if (!windowContains(this.built, needed)) {
      this.rebuild(visibleCellWindow(source.grid, bounds, PAD_CELLS), source);
    }
    if (this.quads === 0) return;

    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.enable(gl.DEPTH_TEST);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.atlas.texture);
    gl.uniform1i(this.uniforms.u_atlas, 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.shadow);
    gl.uniform1i(this.uniforms.u_shadow, 1);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.normal);
    gl.uniform1i(this.uniforms.u_normal, 2);

    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this.height.texture);
    gl.uniform1i(this.uniforms.u_height, 3);

    const cast = characterShadow !== null && characterShadow.ready ? characterShadow : null;
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, cast === null ? this.shadow : cast.texture);
    gl.uniform1i(this.uniforms.u_characterShadow, 4);
    gl.uniform1f(this.uniforms.u_characterShadowOn, cast === null ? 0 : 1);
    gl.uniformMatrix4fv(this.uniforms.u_characterShadowViewProj, false, cast === null ? view.viewProj : cast.viewProj);
    gl.uniform2f(this.uniforms.u_heightTexels, this.height.widthTexels, this.height.heightTexels);
    gl.uniform1f(this.uniforms.u_heightMinMm, this.height.minMm);
    gl.uniform1f(this.uniforms.u_heightSpanMm, this.height.spanMm);

    gl.uniformMatrix4fv(this.uniforms.u_viewProj, false, view.viewProj);
    gl.uniform2f(this.uniforms.u_originMm, source.grid.baseXMm, source.grid.baseYMm);
    gl.uniform1f(this.uniforms.u_cellMm, source.grid.cellMm);
    gl.uniform1f(this.uniforms.u_repeatMm, TEXTURE_REPEAT_MM);
    // In millimetres, for `groundHeightMm`'s own world-to-texel conversion.
    gl.uniform2f(
      this.uniforms.u_worldMm,
      source.grid.worldCellsX * source.grid.cellMm,
      source.grid.worldCellsY * source.grid.cellMm,
    );
    // In metres, to match `v_worldM`. The shadow map covers the grid exactly, so
    // this is also the divisor that turns a world position into its coordinate.
    gl.uniform2f(
      this.uniforms.u_worldM,
      (source.grid.worldCellsX * source.grid.cellMm) / 1000,
      (source.grid.worldCellsY * source.grid.cellMm) / 1000,
    );
    gl.uniform1f(this.uniforms.u_timeS, timeSeconds);
    gl.uniform3f(
      this.uniforms.u_fallback,
      FALLBACK_GROUND[0],
      FALLBACK_GROUND[1],
      FALLBACK_GROUND[2],
    );
    gl.uniform3f(
      this.uniforms.u_slopeFloor,
      this.slopeFloor[0],
      this.slopeFloor[1],
      this.slopeFloor[2],
    );
    gl.uniform2f(this.uniforms.u_fogCameraMm, fog.cameraXMm, fog.cameraYMm);
    gl.uniform2f(this.uniforms.u_fogRangeMm, fog.nearMm, fog.farMm);
    gl.uniform3f(this.uniforms.u_fogColour, fog.colour[0], fog.colour[1], fog.colour[2]);

    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.quads);
    gl.bindVertexArray(null);
  }

  private rebuild(window: CellWindow, source: TerrainSource): void {
    const quads = instanceCount(window);
    const gl = this.gl;

    if (quads > this.capacityQuads) {
      // Grow with headroom so a window that widens by one cell does not
      // reallocate the GPU buffer; a resized window settles within a frame or two.
      this.capacityQuads = Math.ceil(quads * 1.4);
      this.staging = new Uint8Array(this.capacityQuads * INSTANCE_BYTES);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.staging.byteLength, gl.DYNAMIC_DRAW);
    }

    this.quads = buildInstances(
      window,
      source.tiles,
      source.attributes,
      FLAG_BLOCKED,
      this.staging,
    );
    this.built = window;
    if (this.quads === 0) return;

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.staging, 0, this.quads * INSTANCE_BYTES);
  }
}
